import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import FormField from "./FormField";
import { AUTH_HEADERS } from "../constants";
import type { AuthHeader } from "../types";
import { useTranslation } from "react-i18next";

type Props = {
  authHeader: AuthHeader;
  onAuthHeaderChange: (header: AuthHeader) => void;
  oauthClientId: string;
  onOauthClientIdChange: (value: string) => void;
  oauthClientSecret: string;
  onOauthClientSecretChange: (value: string) => void;
  apiKey: string;
  onApiKeyChange: (value: string) => void;
  bearerToken: string;
  onBearerTokenChange: (value: string) => void;
};

const AuthHeaderSelect = ({
  value,
  onChange,
}: {
  value: AuthHeader;
  onChange: (header: AuthHeader) => void;
}) => {
  const { t, i18n } = useTranslation();
  return (
    <FormField label={t("mcpPage.fields.authHeader")} compact>
      <Select
        dir={i18n.dir()}
        value={value}
        onValueChange={(v) => onChange(v as AuthHeader)}
      >
        <SelectTrigger className="h-9 w-full rounded-[8px] border-border bg-card text-foreground">
          <SelectValue placeholder={t("mcpPage.actions.select")} />
        </SelectTrigger>
        <SelectContent>
          {AUTH_HEADERS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {t(`mcpPage.auth.${option.value}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormField>
  );
};

const AuthCredentialFields = ({
  authHeader,
  oauthClientId,
  onOauthClientIdChange,
  oauthClientSecret,
  onOauthClientSecretChange,
  apiKey,
  onApiKeyChange,
  bearerToken,
  onBearerTokenChange,
}: Omit<Props, "onAuthHeaderChange">) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  if (authHeader === "oauth") {
    return (
      <>
        <FormField
          label={t("mcpPage.fields.oauthClientId")}
          htmlFor="oauth-client-id"
          compact
        >
          <Input
            id="oauth-client-id"
            type="text"
            dir={direction}
            autoComplete="off"
            placeholder={t("mcpPage.placeholders.oauthClientId")}
            value={oauthClientId}
            onChange={(e) => onOauthClientIdChange(e.target.value)}
            className="rounded-[8px] bg-card"
          />
        </FormField>
        <FormField
          label={t("mcpPage.fields.oauthClientSecret")}
          htmlFor="oauth-client-secret"
          compact
        >
          <Input
            id="oauth-client-secret"
            type="password"
            dir={direction}
            autoComplete="off"
            placeholder={t("mcpPage.placeholders.oauthClientSecret")}
            value={oauthClientSecret}
            onChange={(e) => onOauthClientSecretChange(e.target.value)}
            className="rounded-[8px] bg-card"
          />
        </FormField>
      </>
    );
  }

  if (authHeader === "api_key") {
    return (
      <FormField
        label={t("mcpPage.fields.apiKey")}
        htmlFor="api-key"
        className="sm:col-span-2"
        compact
      >
        <Input
          id="api-key"
          type="password"
          dir={direction}
          autoComplete="off"
          placeholder={t("mcpPage.placeholders.apiKey")}
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          className="rounded-[8px] bg-card"
        />
      </FormField>
    );
  }

  if (authHeader === "bearer") {
    return (
      <FormField
        label={t("mcpPage.fields.bearerToken")}
        htmlFor="bearer-token"
        className="sm:col-span-2"
        compact
      >
        <Input
          id="bearer-token"
          type="password"
          dir={direction}
          autoComplete="off"
          placeholder={t("mcpPage.placeholders.bearerToken")}
          value={bearerToken}
          onChange={(e) => onBearerTokenChange(e.target.value)}
          className="rounded-[8px] bg-card"
        />
      </FormField>
    );
  }

  return null;
};

const AdvancedSettings = (props: Props) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(true);
  const ChevronIcon = open ? ChevronDown : ChevronRight;
  const hasCredentialFields = props.authHeader !== "none";

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex h-[52px] w-full items-center gap-2 rounded-md text-sm font-medium text-foreground outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
        aria-expanded={open}
        aria-controls="connector-advanced-settings"
      >
        <ChevronIcon
          className={`size-4 text-muted-foreground ${i18n.dir() === "rtl" && !open ? "rotate-180" : ""}`}
        />
        {t("mcpPage.advancedSettings")}
      </button>

      {open && (
        <div
          id="connector-advanced-settings"
          className={`grid gap-2 px-2 pb-2 ${
            hasCredentialFields
              ? "sm:grid-cols-[1fr_2fr_2fr]"
              : "sm:grid-cols-1"
          }`}
        >
          <AuthHeaderSelect
            value={props.authHeader}
            onChange={props.onAuthHeaderChange}
          />
          <AuthCredentialFields {...props} />
        </div>
      )}
    </section>
  );
};

export default AdvancedSettings;
